import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import { formatStringToDate } from '../../../utils/helper';

interface Object_Simple {
    id: number;
    name: string;
}

const formatDate = (date: Date | null) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind 0-basiert
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
};

/**
 * The details of the categories
 */
class EmployeeDialogStore {
    private sdk: ApplicationSdk;

    public activeTab: number = 0;
    public employee_id: number | null = null;

    public employee_firstname: string = '';
    public employee_lastname: string = '';
    public employee_userns: string = '';
    public employee_phonenumber: string = '';
    public employee_reference: string = '';
    public employee_onboarding_date: any = null;
    public employee_offboarding_date: any = null;
    public employee_assigned_objects: Set<number> = new Set();
    public employee_onboarding_now: boolean = false;

    public offboardingEnabled: boolean = false;

    public employeeDataLoading: boolean = false;
    public objectDataLoading: boolean = false;

    public assignedObjects: Set<number> = new Set();
    public unassignedObjects: Set<number> = new Set();
    public availableObjects: Map<number, string> = new Map();

    public trainings: Array<any> = [];

    // Rights
    public employeeAvailableRights: Array<{ name: string; label: string; recommend: boolean }> = [];
    public selectedRights: Array<{ name: string; label: string; recommend: boolean }> = [];

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            activeTab: observable,
            employee_id: observable,
            employee_onboarding_now: observable,
            employee_firstname: observable,
            employee_lastname: observable,
            employee_userns: observable,
            employee_phonenumber: observable,
            employee_assigned_objects: observable,
            employeeDataLoading: observable,
            objectDataLoading: observable,
            assignedObjects: observable,
            employee_offboarding_date: observable,
            employee_onboarding_date: observable,
            unassignedObjects: observable,
            availableObjects: observable,
            employee_reference: observable,
            offboardingEnabled: observable,
            selectedRights: observable,
            trainings: observable,
            fetchEmployees: action,
            fetchObjects: action,
            switchOnboardingNow: action,
            switchOffboarding: action,
            updateObjectAssignment: action,
            onSaveEmployee: action,
            onChangeTab: action,
            onRightsChange: action,
            updateEmployeeAssignment: action,
            assignedObjectsArray: computed,
            unassignedObjectsArray: computed,
            onboardingDate: computed,
            offboardingDate: computed,
            isOffboardingEnabled: computed,
            isLoading: computed,
            isValid: computed,
            lastname: computed,
            firstname: computed,
            validFirstname: computed,
            validPhonenumber: computed,
            validLastname: computed,
            isNewEmployee: computed,
            validOnboarding: computed,
            validReferenceId: computed,
            id: computed,
            allAvailableRights: computed,
        });
        this.sdk = sdk;
    }

    public async init(config: any): Promise<void> {
        if (config?.employee_id) {
            this.employee_id = config.employee_id;
        }

        await this.fetchEmployees(this.employee_id || null);
    }

    public onChangeTab(tab: number): void {
        if (tab === 1) {
            this.fetchTrainings();
        } else if (tab === 0) {
            // this.fetchObjects();
        }
        this.activeTab = tab;
    }

    public async fetchEmployees(id: number | null): Promise<void> {
        runInAction(() => {
            this.employeeDataLoading = true;
        });

        const employeeDetails: any = await this.sdk.fetchApi('employee', 'get', {
            employee_id: id,
        });

        runInAction(() => {
            this.employeeDataLoading = false;

            if (employeeDetails?.status === 200) {
                const { employee, assigned_objects, object_cache, available_permissions } =
                    employeeDetails.json || {};

                if (available_permissions) {
                    this.employeeAvailableRights = available_permissions;
                }

                if (employee) {
                    this.employee_firstname = employee.firstname;
                    this.employee_lastname = employee.lastname;
                    this.employee_phonenumber = employee.phonenumber;
                    this.employee_userns = employee.user_ns;
                    this.employee_onboarding_date = formatStringToDate(employee.onboarding_date);
                    this.employee_offboarding_date = formatStringToDate(employee.offboarding_date);
                    this.employee_onboarding_now = !!employee.onboarding_directly;
                    this.employee_reference = employee.employee_reference || '';
                    this.offboardingEnabled = employee.offboarding_date !== null;
                    this.selectedRights = (employee.permissions || []).map((right: string) => {
                        const rightData = this.employeeAvailableRights.find(
                            (r: any) => r.name === right
                        );
                        return {
                            name: right,
                            label: rightData?.label || right,
                            recommend: rightData?.recommend || false,
                        };
                    });
                } else {
                    // add default values for a new employee
                    this.selectedRights = this.employeeAvailableRights
                        .filter((right: any) => right.recommend)
                        .map((right: any) => ({
                            name: right.name,
                            label: right.label,
                            recommend: right.recommend,
                        }));
                }

                if (assigned_objects) {
                    this.employee_assigned_objects = new Set(
                        assigned_objects.map((employee: any) => employee.id)
                    );

                    assigned_objects.forEach((obj: any) => {
                        if (!this.availableObjects.has(obj.id)) {
                            this.availableObjects.set(obj.id, obj.name);
                        }

                        if (this.employee_assigned_objects.has(obj.id)) {
                            this.assignedObjects.add(obj.id);
                        } else {
                            this.unassignedObjects.add(obj.id);
                        }
                    });
                }
                if (object_cache) {
                    if (this.sdk.objects.needRefresh(object_cache)) {
                        this.fetchObjects();
                    } else {
                        this.updateObjectAssignment(this.sdk.objects.all.data);
                    }
                }
            }
        });
    }

    public async fetchObjects(): Promise<void> {
        const objectList: any = await this.sdk.fetchApi('object', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            this.objectDataLoading = false;

            if (objectList?.status === 200) {
                const allObjects: any = this.sdk.objects.update(
                    objectList.json.data,
                    objectList.json.object_cache
                );
                this.updateObjectAssignment(allObjects || []);
            }
        });
    }

    public async fetchTrainings(): Promise<void> {
        // If there is no employee_id, we don't need to fetch trainings
        if (!this.employee_id) return;

        // Reset
        runInAction(() => {
            // this.productDataLoading = true;
        });

        const trainingList: any = await this.sdk.fetchApi('employee', 'training', {
            employee_id: this.employee_id,
        });

        runInAction(() => {
            if (trainingList?.status === 200) {
                runInAction(() => {
                    this.trainings = (trainingList.json.trainings || []).map((training: any) => ({
                        ...training,
                        image: training.image
                            ? trainingList.json.s3urltraining + training.image + '.jpeg'
                            : null,
                    }));
                });
            }
        });
    }

    public onRightsChange(rights: any): void {
        this.selectedRights = rights;
    }

    public updateObjectAssignment(allObjects: Array<any> = []): void {
        this.availableObjects.clear();
        allObjects.forEach((object: any) => {
            if (!this.availableObjects.has(object.id)) {
                this.availableObjects.set(object.id, object.name);
            }

            // add this object to unassigned if it is not already assigned
            if (this.employee_assigned_objects.has(object.id)) {
                this.assignedObjects.add(object.id);
            } else {
                this.unassignedObjects.add(object.id);
            }
        });
    }
    public async onSaveEmployee(): Promise<void> {
        const useOnboardingDate =
            (this.employee_onboarding_now ? new Date() : this.employee_onboarding_date) || null;
        const useOffboardingDate =
            (this.offboardingEnabled ? this.employee_offboarding_date : null) || null;

        if (this.isNewEmployee && useOffboardingDate && useOffboardingDate <= useOnboardingDate) {
            throw new Error('Das Offboarding Datum muss nach dem Onboarding Datum liegen!');
        }
        const data = {
            firstname: this.firstname,
            lastname: this.lastname,
            phonenumber: this.phonenumber,
            onboarding_directly: this.employee_onboarding_now ? 1 : 0,
            onboarding_date: this.employee_onboarding_now
                ? formatDate(new Date())
                : formatDate(this.employee_onboarding_date) || null,
            offboarding_date: this.offboardingEnabled
                ? formatDate(this.employee_offboarding_date)
                : null,
            assigned_objects: Array.from(this.assignedObjects).map((id: number) => id),
            employee_id: this.employee_id || undefined,
            employee_reference: this.employee_reference || null,
            permissions: this.selectedRights
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((right: any) => right.name),
        };

        const employeeRequest: any = await this.sdk.fetchApi(
            'employee',
            this.employee_id ? 'update' : 'add',
            data
        );

        if (employeeRequest?.status !== 201 && employeeRequest?.status !== 200) {
            const errorCode = employeeRequest?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_VALIDATE_FIRSTNAME':
                        throw new Error('Bitte einen gültigen Vornamen angeben!');
                    case 'ERR_VALIDATE_LASTNAME':
                        throw new Error('Bitte einen gültigen Nachnamen angeben!');
                    case 'ERR_VALIDATE_PHONENUMBER':
                        throw new Error(
                            'Bitte eine gültige Rufnummer angeben. 49(Vorwahl)(Rufnummer)!'
                        );
                    case 'ERR_VALIDATE_ONBOARDING_DATE':
                        throw new Error('Bitte gebe ein Onboarding Datum an!');
                    case 'ERR_DUPLICATED_PHONENUMBER':
                        throw new Error('Diese Rufnummer ist bereits vergeben!');
                    case 'ERR_LICENSE_LIMIT_EXCEEDED':
                        throw new Error('Das Lizenzlimit wurde überschritten!');
                    case 'ERR_NO_AVAILABLE_LICENSES':
                        throw new Error('Keine Lizenzen wurden konfiguriert!');
                    //     case 'ERR_PRODUCT_NOT_ADDED':
                    //         throw new Error('Das Produkt konnte nicht hinzugefügt werden');
                    //     case 'ERR_VALIDATE_CATEGORY_ID':
                    //         throw new Error('Bitte wählen Sie eine Kategorie aus');
                    //     case 'ERR_VALIDATE_NAME':
                    //         throw new Error('Bitte geben Sie einen Produktnamen ein');
                    //     case 'ERR_VALIDATE_ARTICLE_NUMBER':
                    //         throw new Error('Bitte geben Sie eine gültige Artikelnummer ein (1-9999)');
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }

    public updateEmployeeAssignment(
        unassigned: Array<Object_Simple>,
        assigned: Array<Object_Simple>
    ): void {
        this.assignedObjects = new Set(assigned.map(obj => obj.id));
        this.unassignedObjects = new Set(unassigned.map(obj => obj.id));
    }

    public switchOnboardingNow(): void {
        this.employee_onboarding_now = !this.employee_onboarding_now;
    }

    public switchOffboarding(): void {
        this.offboardingEnabled = !this.offboardingEnabled;
    }

    get assignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.assignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?',
            };
        });
    }

    get unassignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.unassignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?',
            };
        });
    }

    set firstname(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.employee_firstname = name;
            });
        }
    }

    get referenceId(): string {
        return this.employee_reference;
    }

    set referenceId(reference: string) {
        // limit to 40 characters
        if (reference.length <= 20) {
            runInAction(() => {
                this.employee_reference = reference;
            });
        }
    }

    get firstname(): string {
        return this.employee_firstname;
    }

    set lastname(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.employee_lastname = name;
            });
        }
    }

    get lastname(): string {
        return this.employee_lastname;
    }

    set phonenumber(phonenumber: string) {
        // limit to 40 characters
        if (phonenumber.length <= 40) {
            runInAction(() => {
                this.employee_phonenumber = phonenumber;
            });
        }
    }

    get phonenumber(): string {
        return this.employee_phonenumber;
    }

    set onboardingDate(date: any) {
        runInAction(() => {
            this.employee_onboarding_date = date || null;
        });
    }

    get onboardingDate(): any {
        if (!this.employee_onboarding_date) return null;
        return this.employee_onboarding_date;
    }

    set offboardingDate(date: any) {
        runInAction(() => {
            this.employee_offboarding_date = date || null;
        });
    }

    get offboardingDate(): Date | null {
        if (!this.employee_offboarding_date) return null;

        return this.employee_offboarding_date;
    }

    get isOnboardingNow() {
        return this.employee_onboarding_now;
    }

    get isOffboardingEnabled() {
        return this.offboardingEnabled;
    }

    get isNewEmployee(): boolean {
        return this.employee_id === null;
    }

    get saveButton(): string {
        return this.employee_id ? 'Aktualisieren' : 'Erstellen';
    }

    get header(): string {
        return this.employee_id ? 'Reinigungskraft bearbeiten' : 'Neue Reinigungskraft anlegen';
    }

    get id(): string {
        return 'employee';
    }

    get isLoading(): boolean {
        return this.employeeDataLoading;
    }

    get validReferenceId(): boolean {
        return this.employee_reference.length <= 20;
    }

    get validFirstname(): boolean {
        return this.employee_firstname.length >= 3;
    }

    get validLastname(): boolean {
        return this.employee_lastname.length >= 3;
    }

    get validPhonenumber(): boolean {
        const phonePattern = /^49(?!0)\d{9,13}$/;
        return phonePattern.test(this.employee_phonenumber);
    }

    get validOnboarding(): boolean {
        return this.employee_onboarding_date !== null || this.employee_onboarding_now;
    }

    get isValid(): boolean {
        return (
            this.validFirstname &&
            this.validLastname &&
            this.validPhonenumber &&
            this.validOnboarding
        );
    }

    get allAvailableRights(): Array<any> {
        return this.employeeAvailableRights;
    }
}

export default EmployeeDialogStore;
